/**
 * Call to Action class. CTA is a generic class
 */
import AssetWrapper from '../asset/wrappers';
import { appendTagToElement, createStyleTag, CssVariables, StyleTag } from '../util/create-style-tag';
import Asset from '../asset/asset';

export type CtaStyle =
  | 'slim'
  | 'standard'
  | 'mini'
  | 'oval'
  | 'estimate-payment'
  | 'cfmoto-summary'
  | 'banner'
  | 'sidebar';

/**
 * Base class for all Call-To-Action classes to extend. This is not abstract to avoid certain
 * typing issues later, where we want to initialize a "Cta" variable type.
 */
export default abstract class Cta<AssetType extends Asset = Asset> {
  public style: CtaStyle = 'standard';
  // List of callback functions when CTA is clicked
  protected onClickCallbacks: ((e: MouseEvent) => void)[] = [];
  public shadowHost: HTMLElement;
  public shadowRoot: ShadowRoot;

  constructor(public wrapper: AssetWrapper<AssetType>) {
    // Shadow host: The regular DOM node that the shadow DOM is attached to.
    this.shadowHost = document.createElement('div');
    this.shadowHost.classList.add('ol-cta-container');
    // Shadow root: The root node of the shadow tree.
    this.shadowRoot = this.shadowHost.attachShadow({ mode: 'open' });
    // Shadow tree: The DOM tree inside the shadow DOM.
    // Shadow boundary: the place where the shadow DOM ends, and the regular DOM begins.
  }

  /**
   * Add the CTA to the asset container, also add required style/link tags in the document head.
   */
  render(_e?: Event, force?: boolean, style?: CtaStyle): void {
    if (!this.isRendered() || force) {
      // Try to get render location first,
      // because this can throw an error if the page isn't ready yet
      const renderLocation = this.wrapper.getRenderLocation(style);
      this.shadowRoot.innerHTML = '';
      this.initStyles();
      this.shadowRoot.appendChild(this.generateHTML());
      renderLocation.appendChild(this.shadowHost);
    }
  }

  /**
   * Removes the CTA from being displayed on the webpage.
   */
  remove(): void {
    this.shadowRoot.innerHTML = '';
  }

  /**
   * Return the style tags that will be added to the CTA ShadowDOM
   */
  abstract returnCtaStylesTags(): StyleTag[];

  /**
   * Return the style/link elements that will be added to the document head element
   */
  abstract returnDocumentStyleTags(): StyleTag[];

  /**
   * Return the CSS variables that will be added to the CTA ShadowDOM
   */
  abstract returnCssVariables(): CssVariables;

  /**
   * Init the CTA styles and also call the platform object to add styles/links elements if needed
   */
  initStyles(): void {
    this.wrapper.platform.addStyleToDocument(this.returnDocumentStyleTags());
    const ctaStyles = [
      createStyleTag(null, 'cta-css-variables', this.returnCssVariables()),
      ...this.returnCtaStylesTags()
    ];
    for (const styleTag of ctaStyles) {
      appendTagToElement(this.shadowRoot, styleTag);
    }
  }

  /**
   * Build the CTA element.
   * @param asset: Asset to build the CTA element for.
   * @param rate: Rate to build the CTA element for.
   */
  abstract buildCtaElement(asset: AssetType, rate: number): HTMLElement;

  /**
   * Generate the HTML to display the call to action.
   */
  abstract generateHTML(): HTMLElement;

  /**
   * Register a new action to perform when the CTA is clicked
   * @param cb: Callback function to call when the CTA is clicked.
   */
  onClick(cb: (e: MouseEvent) => void) {
    this.onClickCallbacks.push(cb);
  }

  // Return true if the shadowRoot element has any children, which means that the
  // CTA was rendered, otherwise, returns false
  isRendered(): boolean {
    return this.shadowRoot.children.length > 0;
  }

  /**
   * Internal: Handle a click event on the CTA and call the onClick callbacks
   * @param e: Click Event
   * @protected
   */
  protected handleOnClick(e: MouseEvent) {
    // Ensure the asset is as up-to-date as possible before triggering callbacks
    this.wrapper.reparseAssetData();
    e.stopPropagation();
    e.preventDefault();
    for (const cb of this.onClickCallbacks) {
      cb(e);
    }
  }
}
